import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { ServiceLogResponse } from '../@types/service.type';
import {
  getServiceLogsByServiceId,
  hideServiceLog,
  getServiceLogsById,
  updateServiceLogsById,
  createServiceLogsForService,
} from '../api/services';
import { ServiceLogAllKey, ServiceLogOneKey } from '../configs/services';

export const useGetServiceLogsByServiceId = (id: number): UseQueryResult<ServiceLogResponse[]> => {
  return useQuery({
    queryKey: [ServiceLogAllKey, id],
    queryFn: () => getServiceLogsByServiceId(id),
    enabled: id !== 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useGetServiceLogsById = (id: number): UseQueryResult<ServiceLogResponse> => {
  return useQuery({
    queryKey: [ServiceLogOneKey, id],
    queryFn: () => getServiceLogsById(id),
    enabled: false,
  });
};

export const useHideServiceLog = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await hideServiceLog(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey],
      });
    },
  });
};

export const useAddServiceLogsForService = (serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createServiceLogsForService(serviceId, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId],
      });
    },
  });
};

// Update service log input type
interface ServiceLogUpdateData {
  id: number;
  serviceId: number;
  notes?: string;
  isHidden?: boolean;
}

export const useUpdateServiceLogsById = (): UseMutationResult<ServiceLogResponse, Error, ServiceLogUpdateData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceLogUpdateData) => {
      return await updateServiceLogsById(data.id, data);
    },

    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, variables.serviceId],
      });
    },
  });
};
